
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { SvgActionMenuItemConfig, SvgActionMenuOptions } from './types';
import { useEffect, useRef, useState } from 'react';

export type SvgActionMenuItemProps = {
	setTooltip: (key: string, tooltip: string) => void;
	clearTooltip: (key: string) => void;
	active: boolean;
	index: number;
	totalItems: number;
	item: SvgActionMenuItemConfig;
	menuOptions: SvgActionMenuOptions;
};

export const SvgActionMenuItem = (props: SvgActionMenuItemProps): React.ReactElement => {
	const { active, index, totalItems } = props;
	const { icon = null, label, tooltip, handler } = props.item;
	const { itemButtonRadius, itemButtonPadding, menuButtonPadding, menuButtonRadius } = props.menuOptions;

	const [hover, setHover] = useState(false);
	const refGroup = useRef(null);
	const refButton = useRef(null);

	useEffect(() => {
		if (hover) {
			props.setTooltip(index.toString(), tooltip);
		} else {
			props.clearTooltip(index.toString());
		}
	}, [hover]);

	// alternate between left and right
	const position = index; // === 0 ? 0 : ((index) % 2 ? -1 : 1) * Math.floor((index) / 2);

	// radius of the menu button + padding between + radius of the item (this) button
	const db = menuButtonRadius + menuButtonPadding + itemButtonRadius;

	// minAngle keeps the buttons from overlapping
	const minRadian = Math.sin((itemButtonRadius + (itemButtonPadding / 2)) / db) * 2;
	const maxButtons = Math.floor((Math.PI * 2) / minRadian);
	const levelCalc = (index) / maxButtons;
	const level = index === 0
		? 0
		: levelCalc - Math.floor(levelCalc) === 0 ? Math.floor(levelCalc) - 1 : Math.floor(levelCalc);

	// const radian = (position * minRadian) + (level * (minRadian / 2)) - ((totalItems % 2) === 0 ? 0 : minRadian / 2);
	const radian = (position * minRadian) + (level * (minRadian / 2)) - ((totalItems % 2) === 0 ? 0 : minRadian / 2);

	// calculate position around the menu item
	const xb = (db + (level * itemButtonRadius * 1.25)) * Math.sin(radian);
	const yb = (db + (level * itemButtonRadius * 1.25)) * Math.cos(radian);
	// console.log('action-menu-item:min-radian:', index, tooltip, minRadian, maxButtons, level, radian, xb, yb);

	useGSAP(() => {
		// console.log('action-menu-item:use-gsap:', itemButtonRadius, index, active, hover, xb, yb);
		gsap.to(refGroup.current, {
			x: active ? xb : 0,
			y: active ? yb : 0,
			opacity: active ? 1 : 0,
			duration: 0.5,
			ease: 'circ.inOut'
		});

		gsap.to(refButton.current, {
			stroke: hover ? 'green' : 'blue',
			opacity: hover ? 1 : 0.45,
			duration: 0.5,
			ease: 'power1.out'
		});

		if (!icon) {
			gsap.timeline()
				.to(`.button-circle-${index}`, {
					attr: {
						r: active ? itemButtonRadius : 0.25
					},
					duration: 0.5,
					ease: 'expo.inOut'
				})
				.to(`.button-text-${index}`, {
					opacity: active ? 1 : 0,
					duration: 0.1,
					ease: 'power.In'
				});
		}
	}, { scope: refGroup, dependencies: [active, hover] });

	return (<g>
		<g ref={refGroup} x={xb} y={yb}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			stroke="none"
			strokeWidth={0.0625}
			style={{
				cursor: 'pointer',
				pointerEvents: 'all'
			}}

			onClick={handler}
		>
			<circle className={`button-circle-${index}`} r={0.25} cx={0} cy={0} fill="white"/>
			<circle ref={refButton}
				className={`button-circle-${index}`}
				cx={0} cy={0} r={0.25}
				fill="lightgray" stroke="blue"
				opacity={0.45}
			/>
			{ icon
				? icon({ cx: 0, cy: 0, r: itemButtonRadius * 0.5, opacity: 0.45 })
				: <text
					className={`button-text-${index}`}
					textAnchor="middle"
					opacity={0}
					x={0} y={active ? itemButtonRadius * 0.3 : 0}
					fontSize={itemButtonRadius}
					fontWeight="normal"
					strokeWidth={0.0625}
					stroke="black" fill="black"
				>{label}</text>
			}
		</g>
	</g>);
};

